import { createFileRoute, Link } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/small-business-marketing-budget-colorado";
const TITLE =
  "How Much Should a Colorado Small Business Spend on Marketing Each Month? (2026)";
const DESCRIPTION =
  "Honest 2026 marketing budget benchmarks for Colorado small businesses by revenue size — with real allocations across website, SEO, Maps, ads, and brand.";

export const Route = createFileRoute("/learn/small-business-marketing-budget-colorado")({
  head: () => learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="Pricing"
      title={TITLE}
      intro="Most Colorado small businesses either under-spend and stay invisible, or over-spend on the wrong channel. Here's the honest 2026 benchmark — by revenue size, by growth goal, with real allocations."
      readTime="6 min read"
      publishedDate="2026-06-22"
      path={PATH}
      definition={{
        term: "How much should a Colorado small business spend on marketing?",
        body: "Most healthy Colorado small businesses spend 7–12% of revenue on marketing — about $4,000–$15,000/month for a $500k–$1.5M business. That covers retainer ($1,650/month for our Growth Marketing program), ad spend ($1,500–$8,000/month), and brand. Below 5% and you stagnate; above 15% only makes sense when actively scaling.",
      }}
      toc={[
        { id: "the-rule", label: "The 7–12% rule" },
        { id: "by-revenue", label: "Budget by revenue size" },
        { id: "by-growth-goal", label: "Budget by growth goal" },
        { id: "allocation", label: "How to split the budget" },
        { id: "our-pricing", label: "What we charge and why" },
      ]}
      faqs={[
        {
          q: "What percentage of revenue should I spend on marketing in Colorado?",
          a: "7–10% for steady-state businesses, 10–15% for businesses actively scaling, 4–6% for mature low-growth businesses defending share. Below 4% is usually under-investment in a competitive Colorado market.",
        },
        {
          q: "Should I spend more on PPC or SEO?",
          a: "Both, in sequence. Start with PPC because it produces leads in weeks. Layer in SEO + Maps because they produce cheaper leads month over month. By year two, SEO and Maps should carry 50–70% of total lead volume.",
        },
        {
          q: "What's the smallest realistic monthly marketing budget for a Colorado service business?",
          a: "About $3,500/month total: $1,650 retainer (our Growth Marketing program) + $1,500 in Google Ads spend. Below that, you can't run both organic and paid effectively in any real Colorado market.",
        },
        {
          q: "When should I cut my marketing budget?",
          a: "Rarely. Cut PPC ad spend in seasonal slow periods, but keep SEO, Maps, and brand running. The biggest losses we see come from owners who pause marketing in slow months and spend the next two quarters rebuilding ground they already paid for.",
        },
      ]}
    >
      <h2 id="the-rule">The 7–12% rule</h2>
      <p>
        Healthy Colorado service businesses spend 7–12% of gross revenue on
        marketing. That includes everything: agency fees, ad spend, brand
        assets, website rebuilds amortized, and tools.
      </p>
      <p>
        Below 5%, you're under-investing. Above 15%, you should either be
        scaling aggressively or defending a high-value position in a saturated
        market.
      </p>

      <h2 id="by-revenue">Budget by revenue size</h2>
      <ul>
        <li><strong>$250k revenue:</strong> $1,800–$2,500/month all-in. Stay simple — SEO + Maps + light PPC.</li>
        <li><strong>$500k revenue:</strong> $3,500–$5,000/month. Our Growth Marketing ($1,650) + $1,500–$3,000 ad spend.</li>
        <li><strong>$1M revenue:</strong> $7,000–$10,000/month. Same retainer, larger ad spend, more content velocity.</li>
        <li><strong>$2M revenue:</strong> $14,000–$20,000/month. Multi-city campaigns or multi-service expansion.</li>
        <li><strong>$5M+ revenue:</strong> $30,000+/month. Statewide, multi-location, brand spend, PR.</li>
      </ul>

      <h2 id="by-growth-goal">Budget by growth goal</h2>
      <ul>
        <li><strong>Defend share, low growth:</strong> 4–6% of revenue.</li>
        <li><strong>Steady 10–20% growth:</strong> 7–10% of revenue.</li>
        <li><strong>Aggressive 30%+ growth:</strong> 10–15% of revenue.</li>
        <li><strong>New market or new service launch:</strong> spike to 15–20% for 6–12 months, then normalize.</li>
      </ul>

      <h2 id="allocation">How to split the budget</h2>
      <p>
        Typical healthy allocation for a $1M Colorado service business
        ($8k/month total marketing):
      </p>
      <ul>
        <li><strong>Retainer (SEO + Maps + social + PPC mgmt):</strong> $1,650/month (~26%).</li>
        <li><strong>Google Ads spend:</strong> $4,500/month (~56%).</li>
        <li><strong>Brand / content / video:</strong> $1,250/month (~10%).</li>
        <li><strong>Tools + reputation system:</strong> $600/month (~8%).</li>
      </ul>
      <p>
        Adjust based on vertical. Roofing and law shift more to ad spend.
        Carpet cleaning and landscaping shift more to SEO/Maps.
      </p>

      <h2 id="our-pricing">What we charge and why</h2>
      <p>
        Our public pricing fits the 7–12% rule for most Colorado small
        businesses:
      </p>
      <ul>
        <li><strong>Websites:</strong> $1,850–$5,950 one-time, depending on page count.</li>
        <li><strong>Monthly Marketing:</strong> $1,250–$3,500/month, month-to-month.</li>
      </ul>
      <p>
        Ad spend is billed directly by Google or Meta — you see every dollar.{" "}
        <Link to="/pricing" className="text-primary underline">See full pricing</Link>.
      </p>
      <p>
        Industry-specific budgets:{" "}
        <Link to="/learn/hvac-marketing-cost-colorado" className="text-primary underline">HVAC</Link>,{" "}
        <Link to="/learn/roofing-marketing-cost-colorado" className="text-primary underline">roofing</Link>,{" "}
        <Link to="/learn/plumber-marketing-cost-colorado" className="text-primary underline">plumbing</Link>,{" "}
        <Link to="/learn/landscaper-marketing-cost-colorado" className="text-primary underline">landscaping</Link>,{" "}
        <Link to="/learn/dental-marketing-cost-colorado" className="text-primary underline">dental</Link>,{" "}
        <Link to="/learn/law-firm-marketing-cost-colorado" className="text-primary underline">law firms</Link>,{" "}
        <Link to="/learn/carpet-cleaning-marketing-cost-colorado" className="text-primary underline">carpet cleaning</Link>,{" "}
        <Link to="/learn/contractor-marketing-cost-colorado" className="text-primary underline">contractors</Link>.
      </p>
    </LearnArticleLayout>
  );
}
